import type { SectionRow, SettingsRow } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

import {
    FastForward,
    Heart,
    PenLine,
    RotateCcw
} from 'lucide-react'

import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

type Phase = 'waiting' | 'typing' | 'done'

interface LetterSectionProps {
    section?: SectionRow
    settings: SettingsRow
}

const delayFor = (character: string, position: number): number => {
    if (character === '\n') return 300
    if (/[.!?]/.test(character)) return 300
    if (/[,:;]/.test(character)) return 150
    if (character === ' ') return 18

    return 22 + (position % 5) * 4
}

export default function LetterSection({ section, settings }: LetterSectionProps) {
    const body = settings.letter_body ?? ''
    const [phase, setPhase] = useState<Phase>('waiting')
    const [count, setCount] = useState(0)
    const paperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (phase !== 'waiting') return

        const node = paperRef.current

        if (!node) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof IntersectionObserver === 'undefined') {
            setPhase('done')

            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setPhase('typing')
                observer.disconnect()
            }
        }, { threshold: 0.3 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [phase])

    useEffect(() => {
        if (phase !== 'typing') return

        if (count >= body.length) {
            setPhase('done')

            return
        }

        const timer = window.setTimeout(() => setCount(value => value + 1), delayFor(body[count], count))

        return () => window.clearTimeout(timer)
    }, [phase, count, body])

    const skip = () => {
        setCount(body.length)
        setPhase('done')
    }

    const replay = () => {
        setCount(0)
        setPhase('typing')
    }

    const done = phase === 'done'

    return (
        <section id='letter' className='relative overflow-hidden bg-gradient-to-b from-cream via-blush/70 to-cream py-24 sm:py-32'>
            <div className='relative mx-auto max-w-6xl px-6 lg:px-10'>
                <SectionHeading section={section} />

                <Reveal className='mx-auto mt-14 max-w-2xl sm:mt-16' direction='zoom'>
                    <div
                        ref={paperRef}
                        className='letter-paper relative rounded-md px-6 pt-16 pb-10 shadow-[0_40px_80px_-40px_rgba(91,21,48,0.55)] ring-1 ring-[#f1d6dd] sm:-rotate-1 sm:px-14 sm:pt-20 sm:pb-14'
                    >
                        <span aria-hidden className='absolute -top-7 left-1/2 grid size-14 -translate-x-1/2 place-items-center rounded-full bg-gradient-to-br from-petal to-petal-deep shadow-lg ring-4 ring-cream'>
                            <Heart className='size-6 fill-cream text-cream' />
                        </span>

                        {settings.letter_greeting && (
                            <p
                                data-mdlfy-cms={`settings:${settings.id}:letter_greeting`}
                                className='pl-6 font-hand text-[2rem] leading-9 font-semibold text-wine sm:pl-4 sm:text-4xl'
                            >
                                {settings.letter_greeting}
                            </p>
                        )}

                        <div className='mt-4 grid pl-6 font-hand text-[1.4rem] leading-9 text-ink sm:pl-4 sm:text-[1.6rem]'>
                            {done
                                ? (
                                    <p
                                        data-mdlfy-cms={`settings:${settings.id}:letter_body`}
                                        className='col-start-1 row-start-1 whitespace-pre-line'
                                    >
                                        {body}
                                    </p>
                                )
                                : (
                                    <>
                                        <p aria-hidden className='invisible col-start-1 row-start-1 whitespace-pre-line'>
                                            {body}
                                        </p>

                                        <p aria-hidden className='col-start-1 row-start-1 whitespace-pre-line'>
                                            {body.slice(0, count)}

                                            {phase === 'typing' && (
                                                <span className='ml-0.5 inline-block h-[1.05em] w-0.5 translate-y-1 animate-caret-blink bg-petal-deep' />
                                            )}
                                        </p>

                                        <p className='sr-only'>
                                            {body}
                                        </p>
                                    </>
                                )}
                        </div>

                        {settings.letter_signoff && (
                            <p
                                data-mdlfy-cms={`settings:${settings.id}:letter_signoff`}
                                className={cn(
                                    'mt-8 text-right font-script text-4xl text-petal-deep transition-opacity duration-1000 sm:text-5xl',
                                    done ? 'opacity-100' : 'opacity-0'
                                )}
                            >
                                {settings.letter_signoff}
                            </p>
                        )}

                        <div className='mt-8 flex items-center justify-between gap-3 border-t border-dashed border-[#f1d6dd] pt-5'>
                            <p className='inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase'>
                                <PenLine aria-hidden className='size-4 text-petal' />
                                {done ? 'Dil se likha' : 'Likh raha hoon'}
                            </p>

                            {done
                                ? (
                                    <button
                                        type='button'
                                        onClick={replay}
                                        className='inline-flex items-center gap-2 rounded-full bg-blush px-4 py-2 text-sm font-semibold text-wine transition outline-none hover:bg-petal hover:text-white focus-visible:ring-4 focus-visible:ring-petal/40'
                                    >
                                        <RotateCcw aria-hidden className='size-4' />
                                        Phir se likho
                                    </button>
                                )
                                : (
                                    <button
                                        type='button'
                                        onClick={skip}
                                        className='inline-flex items-center gap-2 rounded-full bg-wine px-4 py-2 text-sm font-semibold text-cream transition outline-none hover:bg-petal-deep focus-visible:ring-4 focus-visible:ring-petal/40'
                                    >
                                        <FastForward aria-hidden className='size-4' />
                                        Poora padho
                                    </button>
                                )}
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
